import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface ICargoDetailEntityAttributes extends IModelAttributes {
	contactFirstName: string;
	contactLastName: string;
	contactPhone: string;

	cargoId: string;
	cargo: Models.CargoEntity | Models.ICargoEntityAttributes;
	tickets: Array<Models.TicketEntity | Models.ITicketEntityAttributes>;
}
@entity('CargoDetailEntity', 'Cargo Detail')
export default class CargoDetailEntity extends Model implements ICargoDetailEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The first name of the contact for the cargo.
	 */
	@observable
	@attribute<CargoDetailEntity, string>()
	public contactFirstName: string;

	/**
	 * The last name of the contact for the cargo.
	 */
	@observable
	@attribute<CargoDetailEntity, string>()
	public contactLastName: string;

	/**
	 * The phone number of the contact for the cargo.
	 */
	@observable
	@attribute<CargoDetailEntity, string>()
	public contactPhone: string;

	@observable
	@attribute()
	public cargoId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public cargo: Models.CargoEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public tickets: Models.TicketEntity[] = [];

	constructor(attributes?: Partial<ICargoDetailEntityAttributes>) {
		super(attributes);

		this.tickets = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ICargoDetailEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.contactFirstName !== undefined) {
				this.contactFirstName = attributes.contactFirstName;
			}
			if (attributes.contactLastName !== undefined) {
				this.contactLastName = attributes.contactLastName;
			}
			if (attributes.contactPhone !== undefined) {
				this.contactPhone = attributes.contactPhone;
			}
			if (attributes.cargoId !== undefined) {
				this.cargoId = attributes.cargoId;
			}
			if (attributes.cargo !== undefined) {
				if (attributes.cargo === null) {
					this.cargo = attributes.cargo;
				} else if (attributes.cargo instanceof Models.CargoEntity) {
					this.cargo = attributes.cargo;
					this.cargoId = attributes.cargo.id;
				} else {
					this.cargo = new Models.CargoEntity(attributes.cargo);
					this.cargoId = this.cargo.id;
				}
			}
			if (attributes.tickets !== undefined && Array.isArray(attributes.tickets)) {
				if (!this.tickets) {
					this.tickets = [];
				}
				for (const model of attributes.tickets) {
					if (model instanceof Models.TicketEntity) {
						this.tickets.push(model);
					} else {
						this.tickets.push(new Models.TicketEntity(model));
					}
				}
			}
		}
	}
}
